import { Subheader } from "@/Components";
import { BudgetChart, chartColors, getChartColor } from "@/Utilities/V2Graphs";
import {
  LabeledMultiSelectAutocomplete,
  LabeledSelect,
} from "@/Components/Form";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { SCOPE_OPTIONS } from "@/Enums/scopes";
import { InertiaLink, usePage } from "@inertiajs/inertia-react";
import { useLoading } from "@/Contexts/LoadingContext";
import Placeholder from "@/Components/Placeholder";
import { useAtom, useAtomValue } from "jotai";
import { ActionMenu } from "@/Components/ActionMenu";
import { Inertia } from "@inertiajs/inertia";
import { debounce, isEqual } from "lodash";

import {
  budgetGraphModeAtom,
  budgetMunicipalityFilterAtom,
  budgetScopeFilterAtom,
  budgetTeamFilterAtom,
} from "@/Stores";
import {
  convertWeightToHumanReadable,
  percentageValueRecursively,
  refactorNeeded,
  rgb2hex,
  trend,
} from "@/Utilities/Utilities";
import { CheckCircle, Klimatkollen, PauseCircle, StopCircle } from "@/Icons";
import { ChevronDown } from "react-feather";

export const Budget = ({}) => {
  // Don't show teams with less than 6 years reported, since we need at least 5 years not including this year for the graph
  const teams =
    usePage().props?.teams?.data?.filter((team) => team.years_reported >= 5) ||
    [];

  const { isLoaded, setIsLoaded } = useLoading();

  const [emmaData, setEmmaData] = useState({});
  const [categorizedData, setCategorizedData] = useState({});
  const [tags, setTags] = useState([]);
  const [budgetData, setBudgetData] = useState([]);
  const [categorySliders, setCategorySliders] = useState({});
  const [tagNames, setTagNames] = useState([]);

  const municipalityFilter = useAtomValue(budgetMunicipalityFilterAtom);
  const teamFilter = useAtomValue(budgetTeamFilterAtom);
  const scopeFilter = useAtomValue(budgetScopeFilterAtom);
  const graphMode = useAtomValue(budgetGraphModeAtom);

  const currentYear = new Date().getFullYear();
  const years = Object.keys(withoutThisYear(emmaData));
  const yearsReported = years.length;
  const initialYear = Number(years?.[0] ?? currentYear);
  const labels = Array.from(
    { length: 2051 - initialYear },
    (_, i) => initialYear + i,
  ); // [..., 2019, 2020, ..., 2050]

  const actionMenuRef = useRef(null);
  let actionMenuItems = [
    {
      label: "All categories",
      callback: () =>
        Inertia.visit(
          route("dashboard", {
            tagFilter: tags.map((tag) => tag.id).join(","),
          }),
        ),
    },
  ];
  actionMenuItems = actionMenuItems.concat(
    tags.map((tag) => {
      return {
        label: tag.name,
        callback: () => {
          Inertia.visit(
            route("dashboard", {
              tagFilter: tag.id,
            }),
          );
        },
      };
    }),
  );

  /**
   * Fetch data from API for the selected teams and budget data for all municipalities
   */
  const fetchBudgetData = () => {
    if (teamFilter === null) {
      setIsLoaded(true);
      return;
    }

    setIsLoaded(false);

    Promise.all([
      // Fetch municipalities budget data if not already loaded
      budgetData.length === 0
        ? fetch(route("budget.database"))
            .then((response) => response.json())
            .then((data) => {
              setBudgetData(data);
            })
            .catch((error) => console.error(error))
        : null,

      // Get selected teams reported data
      teamFilter && teamFilter.length > 0
        ? fetch(route("budget.data", { teamFilter: teamFilter }))
            .then((response) => response.json())
            .then((data) => {
              // console.log('data', data);
              setEmmaData(data.yearly);
              setCategorizedData(data.categorized);
              setTags(data.budgetCategories);
            })
            .catch((error) => console.error(error))
        : setEmmaData({}),
    ]).then(() => {
      setIsLoaded(true);
    });
  };

  useEffect(() => {
    fetchBudgetData();
  }, []);

  useEffect(() => {
    // const alltags = tags.map((tag) => tag.name);
    // const alltags = [...tags.map((tag) => tag.name), 'No tag'];
    const alltags = [
      ...tags.filter((e) => e.budget).map((tag) => tag.name),
      "Uncategorized emissions",
    ];
    // const alltags = [...tags.map((tag) => tag.name), 'No tag', 'Uncategorized emissions'];
    setTagNames(alltags);
    setCategorySliders(Object.fromEntries(alltags.map((tag) => [tag, 0])));
  }, [tags]);

  const resetSliders = () => {
    // reset all sliders to 0
    setCategorySliders((old) => {
      const newSliders = Object.fromEntries(
        Object.keys(old).map((tag) => [tag, 0]),
      );
      return newSliders;
    });
  };

  const delayedSetCategorySliders = (tag, value) => {
    debounce((tag, value) => {
      setCategorySliders((old) => ({ ...old, [tag]: value }));
    }, 200);
  };

  /**
   * Set the sliders to see the trend
   */
  const seeTrend = () => {
    /**
     * Om vissa kategorier ökar per år men andra minskar?
     * Ska vi endå bara ta totalen som trend och ändra alla katergorier lika mycket
     * så även om en kategori ökar kraftigt kommer den tvärvända och minska i samma takt
     */

    // We change our labels for the linear regression to start from 0 and increment by 1 for each data point
    const trendLine = trend(
      years.map((_, i) => i),
      years.map((year) => calculateTotalAfterFilteringScope(emmaData[year])),
    );

    // Generate the y values for the trend line for every year
    let trendData = labels.map((year, index) => {
      if (index < yearsReported - 1) {
        return null;
      } else {
        const trendValue = trendLine.intercept + trendLine.slope * index;
        if (trendValue < 0) {
          if (belowZeroIndex === false) {
            belowZeroIndex = index;
          }
          return null;
        }

        return trendValue;
      }
    });

    const lastYear = years[years.length - 1];
    const lastYearData = emmaData[lastYear];

    let trendChange = ((trendLine.slope / lastYearData.total) * 100).toFixed(1);
    trendChange = Math.max(-99, Math.min(20, trendChange));

    // reset all sliders to 0
    setCategorySliders((old) => {
      const newSliders = Object.fromEntries(
        Object.keys(old).map((tag) => [tag, trendChange]),
      );
      return newSliders;
    });
  };

  /**
   * Set the sliders to keep the budget
   */
  const keepBudget = (totalBudget = false, yearlyBudgets = false) => {
    if (!totalBudget || !yearlyBudgets) return;

    let sumOfBudgets =
      Object.values(yearlyBudgets).reduce((a, b) => a + b, 0) * 1000;
    const lastYear = Number(currentYear - 1);
    const yearsLeft = 2050 - lastYear;
    const startValue = categorizedData[lastYear]?.total || 0;
    const reductionRate = refactorNeeded(sumOfBudgets, startValue, yearsLeft);

    setCategorySliders((old) => {
      return Object.fromEntries(
        Object.keys(old).map((tag) => [tag, reductionRate]),
      );
    });
  };

  // get each unique municipality from teams
  const municipalities = useMemo(
    () =>
      teams.reduce((acc, team) => {
        if (!acc.includes(team.municipality)) {
          acc.push(team.municipality);
        }
        return acc;
      }, []),
    teams,
  );

  if (isLoaded && Object.keys(withoutThisYear(emmaData)).length < 5) {
    return (
      <NotEnoughHistoricalData
        teams={teams}
        municipalities={municipalities}
        onFilterUpdate={fetchBudgetData}
      />
    );
  }

  /**
   * Remove current year key from an object
   *
   * @param data
   * @returns {any}
   */
  function withoutThisYear(data = {}) {
    const clone = JSON.parse(JSON.stringify(data));
    delete clone[currentYear];
    return clone;
  }

  /**
   * Calculate which percentage we should use based on the selected municipality, average value if more is selected
   *
   * @param municipalities
   * @returns {number}
   */
  const getBudgetPercentageFromSelectedMunicipalities = (municipalities) => {
    let percentage = 0;
    let count = 0;
    municipalities.forEach((municipality) => {
      const municipalityData = budgetData.find(
        (data) => data.Name === municipality,
      );
      if (municipalityData) {
        percentage += municipalityData.EmissionChangePercent;
        count++;
      }
    });

    return percentage / (count || 1);
  };

  /**
   * Calculate the total emission for the selected year after filtering the scopes
   *
   * @param data
   * @returns {number}
   */
  function calculateTotalAfterFilteringScope(data = {}) {
    // loop SCOPE_OPTIONS and check if it is in scopeFilter then add its value to the total
    let total = 0;
    SCOPE_OPTIONS.forEach((scope) => {
      if (!scopeFilter.length || !scopeFilter.includes(scope.value)) {
        return;
      }
      total += data?.[scope.value] || 0;
    });

    return total;
  }

  /**
   * Calculate the total emission for the selected year
   *
   * @param data
   * @returns {number}
   */
  function calculateTotal(data = {}) {
    // loop SCOPE_OPTIONS and check if it is in scopeFilter then add its value to the total
    let total = 0;
    SCOPE_OPTIONS.forEach((scope) => {
      if (!scopeFilter.length || !scopeFilter.includes(scope.value)) {
        return;
      }
      total += data?.[scope.value] || 0;
    });

    return total;
  }

  /**
   * Calculate the trend for this year based on the previous reported data
   *
   * @param existingData
   * @param year
   * @returns {total: number, scope1: number, scope2: number, scope3: number}
   */
  function calculateEmissionForYear(
    existingData,
    year = new Date().getFullYear(),
  ) {
    const keys = Object.keys(withoutThisYear(existingData)).map(Number);

    let index = keys.findIndex((k) => k === year);

    if (index === -1) {
      // If the year is not in the data, we must calculate the theoretical index
      let latestYear = Math.max(...keys);
      index = keys.length + (year - latestYear - 1);
    }

    const data = {};

    SCOPE_OPTIONS.forEach((scope) => {
      const scopeData = keys.map((year) => existingData[year][scope.value]);
      const scopeTrendLine = trend(
        keys.map((_, i) => i),
        scopeData,
      );

      // Set the projection of current year emission
      data[scope.value] =
        scopeTrendLine.intercept + scopeTrendLine.slope * index;
    });

    data.total = calculateTotalAfterFilteringScope(data);

    return data;
  }

  /**
   * Build the datasets for the graph
   * @returns {*[]}
   */
  function buildDataSets() {
    let cloneEmmaData = withoutThisYear(emmaData);

    const budgetCurve = (total, percentage, years, padding) => {
      const data = [];
      for (let i = 0; i < years; i++) {
        if (i + 1 < padding) {
          data.push(null);
          continue;
        }
        data.push(total);
        total = total - total * percentage;
      }
      return data;
    };

    // We change our labels for the linear regression to start from 0 and increment by 1 for each data point
    const trendLine = trend(
      years.map((_, i) => i),
      years.map((year) => calculateTotalAfterFilteringScope(emmaData[year])),
    );

    // Generate the y values for the trend line for every year
    let trendData = labels.map((year, index) => {
      if (index < yearsReported) {
        return null;
      } else {
        const trendValue = trendLine.intercept + trendLine.slope * index;
        if (trendValue < 0) {
          if (belowZeroIndex === false) {
            belowZeroIndex = index;
          }
          return null;
        }

        return trendValue;
      }
    });

    let datasets = [];

    // Add the trend of the current year to the dataset, so it can be visualized in the graph
    cloneEmmaData[currentYear] = calculateEmissionForYear(cloneEmmaData);

    if (graphMode === "future") {
      SCOPE_OPTIONS.forEach((scope) => {
        if (!scopeFilter.length || !scopeFilter.includes(scope.value)) {
          return;
        }

        let row = 0;
        for (let year = currentYear + 1; year <= 2050; year++) {
          row++;

          if (cloneEmmaData[year] === undefined) {
            cloneEmmaData[year] = {};
          }

          cloneEmmaData[year][scope.value] = percentageValueRecursively(
            cloneEmmaData[currentYear][scope.value],
            (-1 / 100) * -1,
            // (tempSlider / 100) * -1,
            row,
          );
        }
      });
    }

    SCOPE_OPTIONS.forEach((scope, index) => {
      // Check if scope.value is in scopeFilter
      if (!scopeFilter.length || !scopeFilter.includes(scope.value)) {
        return;
      }

      datasets.push({
        label: scope.label,
        data: Object.keys(cloneEmmaData).map(
          (year) => cloneEmmaData[year][scope.value],
        ),
        borderColor: getChartColor(index, scope.label),
        backgroundColor: getChartColor(index, scope.label),
        fill: "origin",
        stack: "none",
        pointRadius: 0,
        segment: {
          // borderColor: (ctx) => {
          //   if (
          //     ctx.p0DataIndex === yearsReported - 1 ||
          //     ctx.p1DataIndex === yearsReported
          //   ) {
          //     // Custom fill color for the segment between last year and current year
          //     return ["#94dcde", "#fbb2a3", "#a37ab6", "#dbe2ea"][index % 4];
          //   }
          //   return chartColors[index % chartColors.length];
          // },
          // backgroundColor: (ctx) => {
          //   if (
          //     ctx.p0DataIndex === yearsReported - 1 ||
          //     ctx.p1DataIndex === yearsReported
          //   ) {
          //     // Custom fill color for the segment between last year and current year
          //     return ["#94dcde", "#fbb2a3", "#a37ab6", "#dbe2ea"][index];
          //   }
          //   return chartColors[index % chartColors.length];
          // },
        },
      });
    });

    const createDiagonalPattern = (color = "black") => {
      let shape = document.createElement("canvas");
      shape.width = 16;
      shape.height = 16;
      let c = shape.getContext("2d");
      c.strokeStyle = color;
      c.lineWidth = 5;

      c.beginPath();
      c.moveTo(16, 0);
      c.lineTo(0, 16);
      c.stroke();

      c.beginPath();
      c.moveTo(0, 0);
      c.lineTo(2, 2);
      c.stroke();

      c.beginPath();
      c.moveTo(16, 16);
      c.lineTo(14, 14);
      c.stroke();

      return c.createPattern(shape, "repeat");
    };

    // const chartAreaBorder = {
    //   id: 'chartAreaBorder',
    //   beforeDraw(chart, args, options) {
    //     const {ctx, chartArea: {left, top, width, height}} = chart;
    //     ctx.save();
    //     ctx.strokeStyle = options.borderColor;
    //     ctx.lineWidth = options.borderWidth;
    //     ctx.setLineDash(options.borderDash || []);
    //     ctx.lineDashOffset = options.borderDashOffset;
    //     ctx.strokeRect(left, top, width, height);
    //     ctx.restore();
    //   }
    // };
    // plugins: [chartAreaBorder]

    // Reverse order to get Scope 1 at top and Out of scope in the bottom of the graph
    datasets = datasets.reverse();

    if (graphMode === "current" && scopeFilter.length) {
      datasets.push({
        label: "Trend",
        borderColor: "#868686",
        borderWidth: 2,
        borderDash: [5, 5],
        backgroundColor: createDiagonalPattern("rgba(102, 142, 159, 0.2)"),
        data: trendData,
        fill: "origin",
        pointRadius: 0,
        stack: "single",
        // order: 1,
        // trendTitle:
      });

      datasets.push({
        label: "Paris Agreement",
        data:
          years &&
          budgetCurve(
            calculateTotalAfterFilteringScope(
              emmaData?.[years?.[years.length - 1]],
            ),
            municipalityPercentage / 100,
            labels.length,
            yearsReported,
          ),
        borderColor: getChartColor(4, "Paris Agreement"),
        backgroundColor: getChartColor(4, "Paris Agreement"),
        fill: true,
        pointRadius: 0,
        // order: 10,
      });
    }

    return datasets;
  }

  /**
   * Build the datasets for the graph in future mode, where the user can change the sliders to see the impact for each tag
   * @returns {*[]}
   */
  function buildDataSetsCategorized() {
    let categorizedDatasets = [];
    let categorizedDataParsed = withoutThisYear(categorizedData);

    tagNames.forEach((tag, index) => {
      categorizedDatasets.push({
        label: tag,
        data: Object.keys(categorizedDataParsed).map((year) => {
          // categorizedDataParsed[year]?.emissions[tag]?.total || 0,
          return categorizedDataParsed[year]?.emissions[tag]?.total || 0;
        }),
        borderColor: getChartColor(index, tag),
        backgroundColor: getChartColor(index, tag),
        fill: "origin",
        stack: "none",
        pointRadius: 0,
        segment: {
          borderColor: (ctx) => {
            return getChartColor(index, tag);
          },
          backgroundColor: (ctx) => {
            return getChartColor(index, tag);
          },
        },
      });
    });

    let lastKey = Object.keys(categorizedDataParsed).pop();
    let referenceData = tagNames.map((tag, index) => {
      return categorizedDataParsed[lastKey]?.emissions[tag]?.total || 0;
    });
    for (let year = currentYear, row = 1; year <= 2050; year++, row++) {
      let yearData = tagNames.map((tag, index) => {
        // console.log(tag, index, referenceData[index], categorySliders[tag]);
        let val = percentageValueRecursively(
          referenceData[index],
          (categorySliders[tag] / 100) * -1,
          row,
        );
        categorizedDatasets[index].data.push(val);
        return val;
      });
    }
    // console.log(categorizedDatasets);

    return categorizedDatasets;
  }

  let datasets = [];
  let percentageOver = 0;
  let municipalityPercentage = 0;
  let exceedYear = null;
  let allocation = 0;
  let totalBudget = 0;
  let usedBudgetThisYear = 0;
  let remainingBudget = 0;
  let projectedEmissionThisYear = 0;
  let currentYearTotalBudget = 0;
  let belowZeroIndex = false;
  let yearlyBudgets = {};

  if (isLoaded && teamFilter.length > 0 && municipalities.length > 0) {
    municipalityPercentage = getBudgetPercentageFromSelectedMunicipalities(
      municipalityFilter.length ? municipalityFilter : municipalities,
    );

    // We must check which years are relevant based on the emmaData years

    let teamsTotalReported = 0;
    // let municipalitiesHistoricalEmission = 0;
    let municipalitiesTotalBudget = 0;
    let municipalitiesCount = 0;
    let municipalitiesHistoricalEmission = {};

    let budgetYearsAvailable = [];
    (municipalityFilter.length ? municipalityFilter : municipalities).forEach(
      (municipality) => {
        const municipalityData = budgetData.find(
          (data) => data.Name === municipality,
        );
        if (municipalityData) {
          municipalitiesTotalBudget += municipalityData.Budget;

          /**
           * HistoricalEmissions is an objects with key year and its value
           * - We must check which years are relevant based on the emmaData years
           * - Then if it's a year that is relevant, add it to the total
           */
          municipalityData.HistoricalEmission.forEach((row) => {
            if (years.includes(row.year.toString())) {
              // municipalitiesHistoricalEmission += row.CO2Equivalent;

              // Append the budget data to municipalitiesBudgetData with the year as key, and the value as value (multiplied by 1000)

              if (!municipalitiesHistoricalEmission[row.year]) {
                municipalitiesHistoricalEmission[row.year] = 0;
              }

              municipalitiesHistoricalEmission[row.year] += row.value;

              if (!budgetYearsAvailable.includes(row.year)) {
                budgetYearsAvailable.push(row.year);
              }
            }
          });

          municipalitiesCount++;
        }
      },
    );

    // Add relevant total from emmaData to teamsTotalReported based on municipality budget years
    budgetYearsAvailable.forEach((year) => {
      teamsTotalReported += emmaData[year].total;
    });

    /**
     * The municipalities HistoricalEmission is reported after 2 years so we must calculate this years budget based on the trend
     * - Calculate the trend for municipalitiesBudgetData so its possible to calculate the budget for this year
     * - Calculate what the teams budget for this year based on the trend data
     */
    const trendLine = trend(
      Object.keys(municipalitiesHistoricalEmission).map((year) => Number(year)),
      Object.values(municipalitiesHistoricalEmission),
    );

    // get last key in municipalitiesBudgetData
    const lastReportedBudgetYear = Object.keys(
      municipalitiesHistoricalEmission,
    ).pop();
    for (let i = Number(lastReportedBudgetYear); i < currentYear; i++) {
      municipalitiesHistoricalEmission[i] =
        municipalitiesHistoricalEmission[i - 1] + trendLine.slope;
    }

    const totalMunicipalitiesHistoricalEmission = Object.values(
      municipalitiesHistoricalEmission,
    ).reduce((acc, value) => acc + value, 0);

    /**
     * To get the allocation divide the teams total emissions by the municipalities total emissions
     * - Then divide by 1000 to get the allocation per ton
     */
    allocation =
      teamsTotalReported /
      1000 /
      (totalMunicipalitiesHistoricalEmission / (municipalitiesCount || 1));
    totalBudget = allocation * municipalitiesTotalBudget;
    usedBudgetThisYear = (emmaData[currentYear]?.total || 0) / 1000;

    const reductionRate = 1 - municipalityPercentage / 100;

    // Calculate the amount for the first year
    currentYearTotalBudget =
      (totalBudget * (1 - reductionRate)) /
      (1 - Math.pow(reductionRate, 2050 - currentYear + 1));

    let currentYearBudget = currentYearTotalBudget;

    for (let year = currentYear; year <= 2050; year++) {
      yearlyBudgets[year] = currentYearBudget;
      currentYearBudget *= reductionRate;
    }

    remainingBudget = currentYearTotalBudget - usedBudgetThisYear;

    projectedEmissionThisYear = calculateEmissionForYear(
      withoutThisYear(emmaData),
    );

    // Build the datasets for the graph
    if (graphMode === "future") {
      datasets = buildDataSetsCategorized();
    } else {
      datasets = buildDataSets();
    }

    let totalEmission = 0;
    let yearlyEmission = {};

    // console.log('totalEmission', totalEmission);

    // let lastYearTotal = emmaData[years[years.length - 1]]?.total || 0;
    for (let year = currentYear; year <= 2050; year++) {
      let emissionForYear;

      if (graphMode === "future") {
        const dataIndex = year - initialYear;
        emissionForYear = datasets.reduce(
          (acc, dataset) => {
            // console.log('acc dataIndex', year, acc, dataIndex, dataset.data[dataIndex]);
            return { total: acc.total + dataset.data[dataIndex] };
          },
          { total: 0 },
        );
      } else {
        emissionForYear = calculateEmissionForYear(
          withoutThisYear(emmaData),
          year,
        );
      }

      yearlyEmission[year] = emissionForYear.total / 1000; // Convert from KG to tonnes
      totalEmission += yearlyEmission[year] > 0 ? yearlyEmission[year] : 0;
      // console.log('totalEmission');
      // console.log('totalEmission', year, totalBudget - totalEmission);

      if (exceedYear === null && totalEmission > totalBudget) {
        // console.log("We exceeded the budget in year", year, totalBudget - totalEmission);
        exceedYear = year;
      }
    }

    // console.log('yearlyEmission', yearlyEmission, Object.values(yearlyEmission).reduce((a, b) => a + b, 0));

    // remove years after we exceed the budget on future graph
    if (exceedYear !== null && exceedYear > currentYear) {
      for (
        let dataSetIndex = 0;
        dataSetIndex < datasets.length;
        dataSetIndex++
      ) {
        for (
          let dataIndex = exceedYear - initialYear;
          dataIndex < datasets[dataSetIndex].data.length;
          dataIndex++
        ) {
          if (
            graphMode !== "future" &&
            datasets[dataSetIndex].label !== "Trend"
          ) {
            // only cut trend in other graphs
            continue;
          }
          datasets[dataSetIndex].data[dataIndex] = null;
        }
      }
    }

    percentageOver = ((totalEmission - totalBudget) / totalBudget) * 100;

    // console.log('buildDataSets', datasets);
    // console.log("Yearly budget", yearlyBudgets);
    // console.log("Allocation", allocation);
    // console.log("Our total budget", totalBudget);
    // console.log("Used remaining", usedBudgetThisYear);
    // console.log("Budget remaining", remainingBudget);
    // console.log("Projected emission this year", projectedEmissionThisYear);

    // console.log(`Totala utsläpp: ${totalEmission}`);
    // console.log(`Total budget: ${totalBudget}`);
    // console.log(`Det år vi överskred budgeten: ${exceedYear}`);
    // console.log(`Procent vi är över vid året 2050: ${percentageOver}`);
  }

  return (
    <>
      <Subheader>YOUR CO₂-BUDGET</Subheader>
      <div className="flex flex-col flex-wrap bg-gray-light p-8 shadow-sm xl:flex-row">
        <BudgetFilter
          teams={teams}
          municipalities={municipalities}
          onFilterUpdate={fetchBudgetData}
        />
        <div
          className={`pr-4 ${graphMode === "current" ? "xl:w-full" : "xl:w-2/3"}`}
        >
          {isLoaded ? (
            <>
              <BudgetBanner exceedYear={exceedYear} />
              <BudgetChart
                dataSets={datasets}
                labels={labels}
                exceedYear={exceedYear}
              />
            </>
          ) : (
            <Placeholder width="90%" height="450px" className="mt-4" />
          )}
        </div>
        {graphMode === "future" ? (
          <div className="xl:w-1/3">
            <h2 className="text-xl font-semibold">Change the future</h2>
            <p>
              Try out different scenarios to see where your climate actions
              would have the biggest effect
            </p>
            {tagNames
              .filter((e) => e !== "Uncategorized emissions")
              .map((tag, i) => (
                <div key={`tag_${i}`}>
                  <div className="flex justify-between">
                    <label
                      htmlFor={`tag_${i}_input`}
                      className="block text-sm font-medium"
                    >
                      {/* <span className="mr-2 inline-block h-2 w-2 bg-primary"></span> */}
                      <span
                        className={`mr-2 inline-block h-2 w-2`}
                        style={{
                          background: `${rgb2hex(chartColors[i] || "rgb(0,0,0)")}`,
                        }}
                      ></span>
                      {tag}
                    </label>

                    <span className="block text-sm font-medium">
                      {categorySliders[tag]} %
                    </span>
                  </div>
                  <input
                    id={`tag_${i}_input`}
                    type="range"
                    min="-100"
                    max="20"
                    step={0.1}
                    className="w-full"
                    value={categorySliders[tag]}
                    onChange={(e) =>
                      setCategorySliders((old) => ({
                        ...old,
                        [tag]: e.target.value,
                      }))
                    }
                  />
                </div>
              ))}
            <div className="mt-4 flex flex-row flex-nowrap">
              <button
                className="btn-outline mr-1 border-primary p-2"
                onClick={() => keepBudget(totalBudget, yearlyBudgets)}
              >
                Keep budget
              </button>
              <button
                className="btn-outline mr-1 border-primary p-2"
                onClick={() => seeTrend()}
              >
                See trend
              </button>
              <button
                className="p-2"
                onClick={(e) => {
                  e.preventDefault();
                  actionMenuRef.current.toggleMenu();
                }}
              >
                <span className="flex flex-nowrap">
                  View in results <ChevronDown size={24} className="ml-2" />
                </span>
                <div className="hover-circle relative z-10 ml-2">
                  <ActionMenu ref={actionMenuRef} items={actionMenuItems} />
                </div>
              </button>
            </div>
          </div>
        ) : (
          <div className="flex w-full flex-col gap-4 pt-4 sm:flex-row">
            <InfoBox
              currentYear={currentYear}
              percentageOver={percentageOver}
              exceedYear={exceedYear}
              netZeroYear={
                belowZeroIndex !== false ? labels[belowZeroIndex] : null
              }
              percentage={municipalityPercentage}
              allocaction={allocation}
              totalBudget={totalBudget}
              usedBudgetThisYear={usedBudgetThisYear}
              remainingBudget={remainingBudget}
              currentYearTotalBudget={currentYearTotalBudget}
              projectedEmissionThisYear={
                (projectedEmissionThisYear?.total || 0) / 1000
              } // TODO: This is based on previous years, we should probably base it on months instead
            />
          </div>
        )}
        <div className="mt-4 flex w-full flex-row items-center border-t-2 border-gray-100 pt-4">
          <Klimatkollen className="mr-3 mt-[3px]" />
          <p>
            Budget calculations rely on data sourced from klimatkollen.{" "}
            <a
              className="underline hover:no-underline"
              target="_BLANK"
              href="https://www.klimatkollen.se/kallor-och-metod"
            >
              Read more here
            </a>
          </p>
        </div>
      </div>
    </>
  );
};

const BudgetBanner = (exceedYear, className = "") => {
  const currentYear = new Date().getFullYear();
  const exceededByYear = exceedYear?.exceedYear
    ? parseInt(exceedYear?.exceedYear) - parseInt(currentYear)
    : false;
  // const exceededByYear = false;
  // console.log('exceedYear', exceedYear?.exceedYear, currentYear, exceededByYear);

  return (
    <div className="mb-4">
      {exceededByYear === false ? (
        <div className="mb-4 flex justify-start bg-[#E5F1ED] p-4">
          <CheckCircle size={24} />
          <div className="pl-2">
            <h3 className="text-md font-bold">
              This scenario is in line with the Paris Agreement.
            </h3>
            <p>
              In this scenario, the cumulative emissions are kept within the
              budget.
            </p>
          </div>
        </div>
      ) : exceededByYear > 12 ? (
        <div className="mb-4 flex justify-start bg-[#FFF5E5] p-4">
          <PauseCircle size={24} />
          <div className="pl-2">
            <h3 className="text-md font-bold">
              This scenario is deemed not to be in line with the Paris
              Agreement.
            </h3>
            <p>
              In this scenario, the budget is enough for a while, but not for
              the entire budget period.
            </p>
          </div>
        </div>
      ) : (
        <div className="mb-4 flex justify-start bg-[#FEE5E5] p-4">
          <StopCircle size={24} />
          <div className="pl-2">
            <h3 className="text-md font-bold">
              This scenario is deemed not to be in line with the Paris
              Agreement.
            </h3>
            <p>
              In this scenario, the budget runs out already within{" "}
              {exceededByYear} years.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const BudgetFilter = ({
  teams = [],
  municipalities = [],
  onFilterUpdate = () => {},
}) => {
  const [teamFilter, setTeamFilter] = useAtom(budgetTeamFilterAtom);
  const [municipalityFilter, setMunicipalityFilter] = useAtom(
    budgetMunicipalityFilterAtom,
  );
  const [scopeFilter, setScopeFilter] = useAtom(budgetScopeFilterAtom);
  const [graphMode, setGraphMode] = useAtom(budgetGraphModeAtom);

  const [shouldRefresh, setShouldRefresh] = useState(false);

  /**
   * Set the default values for the filters if they're null
   */
  useEffect(() => {
    if (teamFilter === null && teams.length > 0) {
      teamRef.current.setStateOptions((prev) =>
        prev.map((team) => ({
          ...team,
          checked: true,
        })),
      );
    }
  }, []);

  useEffect(() => {
    if (shouldRefresh) {
      setShouldRefresh(false);
      onFilterUpdate();
    }
  }, [shouldRefresh]);

  const onSelectTeams = (val) => {
    let updatedFilterValues = [];

    if (val.length) {
      updatedFilterValues = municipalities.filter(
        (municipality) =>
          teams.findIndex(
            (t) =>
              t.municipality === municipality &&
              val.findIndex((v) => v.value === t.id) !== -1,
          ) !== -1,
      );
    } else {
      updatedFilterValues = [];
    }

    if (
      municipalityRef?.current &&
      !isEqual(municipalityFilter, updatedFilterValues)
    ) {
      // Uncheck any municipalities not in the selected teams
      municipalityRef.current.setStateOptions((prev) => {
        return prev.map((municipality) => {
          municipality.checked = updatedFilterValues.includes(
            municipality.value,
          );
          return municipality;
        });
      });

      setMunicipalityFilter(updatedFilterValues);
    }

    const newValue = val.map((v) => v.value);

    if (!isEqual(teamFilter, newValue)) {
      setTeamFilter(newValue);
      setShouldRefresh(true);
    }
  };

  const onSelectMunicipalities = (val) => {
    // Uncheck any teams not in the selected municipalities
    let updatedFilterValues = [];

    if (val.length) {
      let selectedTeams = teamRef.current.stateOptions
        .filter((team) => team.checked)
        .map((team) => team.value);
      let selectedMunicipalities = teams
        .filter((team) => selectedTeams.includes(team.id))
        .map((team) => team.municipality);
      selectedMunicipalities = [...new Set(selectedMunicipalities)];

      val.forEach((municipality) => {
        if (!selectedMunicipalities.includes(municipality.value)) {
          const teamsWithMunicipality = teams
            .filter((team) => team.municipality === municipality.value)
            .map((team) => team.id);
          updatedFilterValues = [
            ...updatedFilterValues,
            ...teamsWithMunicipality,
          ];
        } else {
          updatedFilterValues = [
            ...updatedFilterValues,
            ...selectedTeams.filter(
              (team) =>
                teams.find((t) => t.id === team).municipality ===
                municipality.value,
            ),
          ];
        }
      });
    } else {
      updatedFilterValues = [];
    }

    if (teamRef?.current && !isEqual(teamFilter, updatedFilterValues)) {
      teamRef.current.setStateOptions((prev) => {
        return prev.map((team) => {
          team.checked = updatedFilterValues.includes(team.value);
          return team;
        });
      });

      setTeamFilter(updatedFilterValues);
    }

    const newValue = val.map((v) => v.value);

    if (!isEqual(municipalityFilter, newValue)) {
      setMunicipalityFilter(newValue);
      setShouldRefresh(true);
    }
  };

  const municipalityRef = useRef(null);
  const teamRef = useRef(null);

  return (
    <div className="mb-8 w-full grow">
      <div className="flex flex-wrap">
        <LabeledMultiSelectAutocomplete
          ref={municipalityRef}
          title="Location"
          className="mb-4 w-1/2 pr-2 text-gray-700 sm:w-1/4 lg:w-48"
          placeholder="Locations"
          defaultValue={municipalityFilter}
          options={municipalities.map((municipality) => ({
            value: municipality,
            label: municipality,
          }))}
          onSelect={onSelectMunicipalities}
        />
        <LabeledMultiSelectAutocomplete
          ref={teamRef}
          title="Team"
          className="mb-4 w-1/2 pl-2 text-gray-700 sm:w-1/4 sm:pr-2 lg:w-48"
          placeholder={`Teams`}
          options={teams.map((team) => ({ value: team.id, label: team.name }))}
          defaultValue={teamFilter}
          onSelect={onSelectTeams}
        />
        <LabeledMultiSelectAutocomplete
          title="Scope"
          className="mb-4 w-1/2 pr-2 text-gray-700 sm:w-1/4 sm:pl-2 lg:w-48"
          placeholder="Scopes"
          options={SCOPE_OPTIONS}
          defaultValue={scopeFilter.map((scope) => scope?.value || scope)}
          onSelect={(val) => setScopeFilter(val.map((v) => v.value))}
        />
        <LabeledSelect
          title="Visualization"
          containerClass="mb-4 text-gray-700 w-1/2 sm:w-1/4 lg:w-48 pl-2"
          className="w-full"
          noDefault={true}
          options={[
            { value: "current", label: "Current status" },
            { value: "future", label: "Change the future" },
          ]}
          value={graphMode}
          onChange={(e) => setGraphMode(e.target.value)}
        />
      </div>
    </div>
  );
};

const InfoBox = ({
  exceedYear,
  percentageOver,
  netZeroYear,
  usedBudgetThisYear,
  totalBudget,
  projectedEmissionThisYear,
  currentYearTotalBudget,
  currentYear,
}) => {
  const { isLoaded } = useLoading();

  const TextPlaceholder = (props) => {
    return isLoaded ? (
      <>{props.children}</>
    ) : (
      <Placeholder width={`${props.width}px`} height={`${props.height}px`} />
    );
  };

  return (
    <>
      <div className="sm:w-1/3 lg:w-full">
        <h3 className="text-[18px] font-semibold leading-[1.3]">
          <TextPlaceholder width={310} height={90}>
            <p>
              {!exceedYear
                ? "You will successfully meet the Paris agreement budget by 2050 according to current trend."
                : "You will not meet the Paris agreement budget by 2050 according to current trend."}
            </p>
            <p>
              You have used{" "}
              {Math.round((usedBudgetThisYear / currentYearTotalBudget) * 100)}%
              of this years budget.
            </p>
          </TextPlaceholder>
        </h3>
      </div>
      <div className="w-full sm:w-1/3 lg:w-full">
        <h6 className="mb-2 text-[14px] uppercase leading-none">
          <TextPlaceholder width={70} height={16}>
            This year
          </TextPlaceholder>
        </h6>
        <TextPlaceholder width={310} height={140}>
          <p>
            Emissions during {currentYear} is{" "}
            {convertWeightToHumanReadable(usedBudgetThisYear * 1000)?.concat}{" "}
            CO₂e and remaining budget {currentYear} is{" "}
            {
              convertWeightToHumanReadable(
                (currentYearTotalBudget - usedBudgetThisYear) * 1000,
              )?.concat
            }{" "}
            CO₂e.
          </p>
          <p>
            Projected total emissions for this year is{" "}
            {
              convertWeightToHumanReadable(projectedEmissionThisYear * 1000)
                ?.concat
            }{" "}
            CO₂e.
            {projectedEmissionThisYear > currentYearTotalBudget
              ? ` To be in line with the budget this year you need to lower emissions with ${Math.round(
                  ((projectedEmissionThisYear - currentYearTotalBudget) /
                    currentYearTotalBudget) *
                    100,
                )}% or ${
                  convertWeightToHumanReadable(
                    (projectedEmissionThisYear - currentYearTotalBudget) * 1000,
                  )?.concat
                } CO₂e`
              : ` If you keep this trend during the remaining period you will achieve the budget goal for ${currentYear}.`}
          </p>
        </TextPlaceholder>
      </div>
      <div className="sm:w-1/3 lg:w-full">
        <h6 className="mb-2 text-[14px] uppercase leading-none">
          <TextPlaceholder width={100} height={16}>
            Total budget
          </TextPlaceholder>
        </h6>
        <TextPlaceholder width={310} height={140}>
          <p>
            Total budget until year 2050 is{" "}
            {convertWeightToHumanReadable(totalBudget * 1000, true)?.concat}{" "}
            CO₂e.
          </p>
          {exceedYear ? (
            <>
              <p>
                Historic and current emission trend shows that your team will
                never meet the 2050 budget target.
              </p>
              <p>
                If the trend continues your budget ends {exceedYear} and you
                will exceed the budget with {Math.round(percentageOver)}% 2050
              </p>
            </>
          ) : (
            <p>
              Historic and current emission trend shows that your team is in
              line with the Paris Agreement reduction targets. If the trend
              continues you will reach the budget goal by {netZeroYear}.
            </p>
          )}
        </TextPlaceholder>
      </div>
    </>
  );
};

const NotEnoughHistoricalData = ({
  teams,
  municipalities,
  onFilterUpdate = () => {},
}) => {
  return (
    <>
      <Subheader>YOUR CO₂-BUDGET</Subheader>
      <div className="flex flex-wrap bg-gray-light p-8">
        <BudgetFilter
          teams={teams}
          municipalities={municipalities}
          onFilterUpdate={onFilterUpdate}
        />
        <div className="relative flex w-full flex-col items-center justify-evenly gap-8 bg-yellow p-4 sm:aspect-[64/45] sm:flex-row sm:px-8 md:max-h-[450px]">
          <div className="flex-grow sm:max-w-[50%] lg:max-w-[50%]">
            {teams.length === 0 ? (
              <>
                <h2 className="text-xl font-semibold">
                  We need your historical data.
                </h2>
                <p>
                  Include a minimum of 5 years of historical data for your teams
                  to uncover insights from this report.
                </p>
                <InertiaLink
                  href={route("teams")}
                  className="btn-blue my-4 inline-block"
                >
                  Add historical data
                </InertiaLink>
                <p>
                  Or{" "}
                  <a
                    className="text-blue underline hover:no-underline"
                    href="mailto:emma@gidas.se"
                  >
                    contact us
                  </a>{" "}
                  to get help from our experts.
                </p>
              </>
            ) : (
              <>
                <h2 className="text-xl font-semibold">No teams selected</h2>
                <p>
                  Choose location and team to se how your climate work is going
                  in your organisation
                </p>
              </>
            )}
          </div>
          <img
            className="sm:max-w-[50%] md:max-h-[418px]"
            src={
              teams.length === 0
                ? "/images/budget-background.png"
                : "/images/budget-background-select-teams.svg"
            }
          />
        </div>
      </div>
    </>
  );
};
